import { keyBy, merge, values } from "lodash";
import { actionTypes } from "redux-query";
import { deleteCashflowCustomModel } from "api/admin/src";
import { deletePortfoliosRaw } from "api/ux/src";
import { InsBenchmarkCreationBlendedGridId, preview } from "common/constants";
import { ASSETS_PAGE, FUNDS_PAGE, MANAGERS_PAGE, PORTFOLIOS_PAGE } from "common/constants/routes";
import { changeHash } from "common/object-helper";
import { ModalName } from "components/modal/types";
import { getDatapointGridStateId } from "components/widgets/grid/common";
import { CustomColumnType } from "components/widgets/grid/grid-column-types";
import { DashboardTypes } from "components/workspaces/hooks/types";
import { dashboardPageTypeDefinitionsByType } from "components/workspaces/hooks/use-get-dashboards-by-type";
import { DatapointGroupingType } from "datapoints";
import { selectDimensionsBasedOnMeasures } from "datapoints/selectDimensionsBasedOnMeasures";
import { clearTaActionPrefix, redoTaActionPrexif, undoTaActionPrexif } from "store/administration/cashflow-forecasting/ta-model-editing";
import { resetBenchmarkCreation } from "store/benchmark-creation";
import { DataScale } from "store/current-dashboard/types";
import { RelativeValueOptions } from "store/widget-workshop/types";
import { isLocationChangeAction } from "store/current-dashboard/utils";
import { addSelectedRows, removeEditedRow, setEditedRow } from "store/grids";
import { datapointsKey } from "store/middleware/analytics/analytics-grouping-middleware";
import { closeModal } from "store/modal";
export const isFundLocationChangeAction = (action) => isLocationChangeAction(action) &&
    action.payload?.location?.pathname?.startsWith(dashboardPageTypeDefinitionsByType[DashboardTypes.Fund].pageLocationPathMatch);
export const isFundSeriesLocationChangeAction = (action) => isLocationChangeAction(action) &&
    action.payload?.location?.pathname?.startsWith(dashboardPageTypeDefinitionsByType[DashboardTypes.FundSeries].pageLocationPathMatch);
export const isPortfolioLocationChangeAction = (action) => isLocationChangeAction(action) &&
    action.payload?.location?.pathname?.startsWith(dashboardPageTypeDefinitionsByType[DashboardTypes.Portfolio].pageLocationPathMatch);
export const isManagerLocationChangeAction = (action) => isLocationChangeAction(action) &&
    action.payload?.location?.pathname?.startsWith(dashboardPageTypeDefinitionsByType[DashboardTypes.Manager].pageLocationPathMatch);
export const isAssetLocationChangeAction = (action) => isLocationChangeAction(action) &&
    action.payload?.location?.pathname?.startsWith(dashboardPageTypeDefinitionsByType[DashboardTypes.Asset].pageLocationPathMatch);
export const isMarketLocationChangeAction = (action) => isLocationChangeAction(action) &&
    action.payload?.location?.pathname?.startsWith(dashboardPageTypeDefinitionsByType[DashboardTypes.Market].pageLocationPathMatch);
export const isCashFlowLocationChangeAction = (action) => isLocationChangeAction(action) &&
    (action.payload?.location?.pathname?.includes("custom-models/") || action.payload?.location?.pathname?.includes("predefined-models/"));
export const isPublicEquityIndicesLocationChangeAction = (action) => isLocationChangeAction(action) &&
    (action.payload?.location?.pathname?.endsWith("administration/market-data") ||
        action.payload?.location?.pathname?.endsWith("administration/market-data/public-equity-indices"));
export const isPrivateBenchmarkLocationChangeAction = (action) => isLocationChangeAction(action) && action.payload?.location?.pathname?.endsWith("market-data/private-equity-benchmarks");
export const isMainGridLocationChangeAction = (action) => isLocationChangeAction(action) &&
    (action.payload?.location?.pathname?.startsWith(PORTFOLIOS_PAGE) ||
        action.payload?.location?.pathname?.startsWith(FUNDS_PAGE) ||
        action.payload?.location?.pathname?.startsWith(ASSETS_PAGE) ||
        action.payload?.location?.pathname?.startsWith(MANAGERS_PAGE));
export const isApplyModelByFundLocationChangeAction = (action) => isLocationChangeAction(action) && action.payload?.location?.pathname?.includes("apply-by-fund");
export const isAddWidgetCloseModalAction = (action) => action.type === closeModal.type && action.payload === ModalName.AddWidget;
export const isActionType = (actionCreator) => (action) => action.type === actionCreator.type;
export const isCashflowModelAssociationChangeReduxQueryAction = (action) => {
    if (action.type === actionTypes.MUTATE_SUCCESS) {
        const queryKey = action.queryKey || "";
        const deleteQueryKey = deleteCashflowCustomModel({ id: "" })?.queryKey || "";
        return queryKey.startsWith(deleteQueryKey.substring(0, deleteQueryKey.indexOf("-")));
    }
    return false;
};
export const isPutModelAssociationReduxQueryAction = (action) => {
    if (action.type === actionTypes.MUTATE_SUCCESS) {
        const queryKey = action.queryKey || "";
        return queryKey.startsWith("PutModelAssociation");
    }
    return false;
};
export const isDeletePortfoliosReduxQueryAction = (action) => {
    if (action.type === actionTypes.MUTATE_SUCCESS) {
        const queryKey = action.queryKey || "";
        const deleteRequest = { deletePortfolioRequest: { portfolioIds: [] } };
        const deleteQueryKey = deletePortfoliosRaw(deleteRequest)?.queryKey || "";
        return queryKey.startsWith(deleteQueryKey.substring(0, deleteQueryKey.indexOf("-")));
    }
    return false;
};
export const resetDatapointsCache = (state) => {
    state.entities.Datapoints = {};
    state.entities.DatapointsFilters = {};
    const keys = Object.keys(state.customQueries).filter((k) => k.includes(datapointsKey) || k.includes("PostFiltersForDatapoints"));
    for (const key of keys) {
        delete state.customQueries[key];
    }
    changeHash();
};
const isReduxMutateAction = (action) => action.type === actionTypes.MUTATE_SUCCESS;
const containsQueryKey = (actionQuery, queryKeys) => {
    const keyRegex = new RegExp(queryKeys.map((key) => key.replace(/[/\-\\^$*+?.()|[\]{}]/gu, "\\$&")).join("|"), "u");
    return keyRegex.test(actionQuery);
};
export const isDatapointsInvalidationAfterLocationChangedAction = (action) => {
    const exitedDashboard = isLocationChangeAction(action) &&
        ["PUSH", "POP"].includes(action.payload.action) &&
        action.payload?.location &&
        action.payload.location.pathname.split("/").length < 3;
    return exitedDashboard;
};
export const isDatapointsInvalidationAction = (action) => {
    const queryKeys = ["PutPortfolio", "PostTenantPrivateBenchmarks", "PostBenchmarksConfiguration", "DeleteTenantPrivateBenchmarks", "PostPortfolioComparisonAssociation"];
    const isMutateWithValidKey = isReduxMutateAction(action) && containsQueryKey(action.queryKey, queryKeys);
    return isMutateWithValidKey;
};
export const isMarketMutationAction = (action) => {
    const queryKeys = ["CreateMarket", "DeleteMarket"];
    const isMutateWithValidKey = isReduxMutateAction(action) && containsQueryKey(action.queryKey, queryKeys);
    return isMutateWithValidKey;
};
export const updateDatapointRequestParamsDimensions = (oldDimensions, newDimensions) => values(merge(keyBy(newDimensions, (item) => item?.standardDatapoint?.toString() + item?.customDatapoint?.toString()), keyBy(oldDimensions, (item) => item?.standardDatapoint?.toString() + item?.customDatapoint?.toString())));
export const isTaUndoRedoAction = (action) => action.type?.includes(undoTaActionPrexif) || action.type?.includes(redoTaActionPrexif) ||
    action.type?.includes(clearTaActionPrefix);
export const isModifyBenchmarkAllocationAction = (action) => (isActionType(addSelectedRows)(action) ||
    isActionType(setEditedRow)(action) ||
    isActionType(removeEditedRow)(action)) && action.payload.id === InsBenchmarkCreationBlendedGridId;
export const isResetBenchmarkCreation = (action) => isActionType(resetBenchmarkCreation)(action);
export const applyOriginalWidgetPipelineParameterVisitorsIntoInspectDataOne = (pipelineParameter, originalWidgetPipelineParameter) => {
    pipelineParameter.datapointsVisitorsReducer = (pipelineParameter.datapointsVisitorsReducer || [])
        .concat(originalWidgetPipelineParameter?.datapointsVisitorsReducer?.filter((v) => v.applyToInspectData === true) || []);
    pipelineParameter.nodesVisitorsReducer = (pipelineParameter.nodesVisitorsReducer || [])
        .concat(originalWidgetPipelineParameter?.nodesVisitorsReducer?.filter((v) => v.applyToInspectData === true) || []);
    pipelineParameter.customVisitorsReducer = (pipelineParameter.customVisitorsReducer || [])
        .concat(originalWidgetPipelineParameter?.customVisitorsReducer?.filter((v) => v.applyToInspectData === true) || []);
    pipelineParameter.multiRequestVisitorsReducer = (pipelineParameter.multiRequestVisitorsReducer || [])
        .concat(originalWidgetPipelineParameter?.multiRequestVisitorsReducer?.filter((v) => v.applyToInspectData === true) || []);
};
export const isMessagesDialogAction = (action) => action.type === "messagesDialogReducers/setMessagesDialog";
export const updateGridUserSettings = (widgetComponentProps, columnsOptions) => {
    if (columnsOptions.some((o) => o?.type === CustomColumnType.InsMoneyColumn)) {
        widgetComponentProps.userSettings = { ...widgetComponentProps.userSettings, dataScale: DataScale?.unit };
    }
    else if (widgetComponentProps.userSettings?.dataScale) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { dataScale, ...otherSettings } = widgetComponentProps.userSettings;
        widgetComponentProps.userSettings = { ...otherSettings };
    }
    else {
        widgetComponentProps.userSettings = widgetComponentProps.userSettings || {};
    }
};
export const applyNewDimensions = (widgetComponentProps, newDimensionsToConsider, groupingOptionsDatapoints) => {
    const currentDimensions = widgetComponentProps.datapointParams[0]?.dimensions;
    const existingDimensionsMap = new Map(currentDimensions?.map((d) => [d.standardDatapoint?.toString(), d]) ?? []);
    widgetComponentProps.datapointParams[0].dimensions = newDimensionsToConsider.map((standardDp) => {
        const existing = existingDimensionsMap.get(standardDp);
        return existing || { name: standardDp, standardDatapoint: standardDp };
    });
    if (groupingOptionsDatapoints) {
        widgetComponentProps.groupingDatapoints = groupingOptionsDatapoints
            .map((m) => ({ ...m, type: newDimensionsToConsider.includes(m.name) ? DatapointGroupingType.Dimension : DatapointGroupingType.MeasureOnly }));
    }
    if (widgetComponentProps.pipelineParameter?.compositeObjectBuilder?.dimensions && widgetComponentProps.datapointParams[0].dimensions) {
        widgetComponentProps.pipelineParameter.compositeObjectBuilder.dimensions = widgetComponentProps.datapointParams[0].dimensions.map((d) => d.name);
    }
    if (widgetComponentProps?.columnsTemplate) {
        const columns = widgetComponentProps.columnsTemplate.columns.map((c) => c);
        widgetComponentProps.datapointParams[0].measures = widgetComponentProps.datapointParams[0].measures
            .map((m) => ({ ...m,
            relativeValueOption: String(columns?.find((c) => c.field === m.name)?.relativeValueOption) }));
    }
};
export const updateGridDimensions = (widgetComponentProps, columnsOptions, allowedDimensionsForCurrentContext, actionType, groupingOptionsDatapoints) => {
    const allowedDimensions = Object.keys(allowedDimensionsForCurrentContext).map((id) => allowedDimensionsForCurrentContext[id]);
    const datapointsAsMeasureFromColumns = columnsOptions
        .filter((co) => co.relativeValueOption !== (RelativeValueOptions.AgainstGrandTotal || RelativeValueOptions.AgainstGroups))
        .map((o) => o.datapoint)
        .filter((t) => t !== undefined);
    if (!allowedDimensions || !datapointsAsMeasureFromColumns ||
        !widgetComponentProps?.pipelineParameter?.compositeObjectBuilder || widgetComponentProps?.datapointParams.length === 0) {
        return;
    }
    const existingDimensions = widgetComponentProps.datapointParams[0]?.dimensions
        ?.map((m) => m.standardDatapoint?.toString()) ?? [];
    const newDimensionsToConsider = selectDimensionsBasedOnMeasures(datapointsAsMeasureFromColumns ?? [], allowedDimensions, existingDimensions, actionType);
    if (newDimensionsToConsider.length !== 0) {
        applyNewDimensions(widgetComponentProps, newDimensionsToConsider, groupingOptionsDatapoints);
    }
};
export const syncPreviewGridColumns = (state, columnsOptions) => {
    const gridStateId = getDatapointGridStateId(preview.sectionId, preview.widgetId);
    if (columnsOptions && state.grids.state[gridStateId]?.columns) {
        const currentGridState = state.grids.state[gridStateId].columns;
        state.grids.state[gridStateId].columns = columnsOptions.map((opt) => {
            const existingGridCol = currentGridState?.find((gc) => gc.colId === opt.id || gc.colId === opt.colId);
            return {
                ...opt,
                colId: opt.id,
                rowGroup: existingGridCol?.rowGroup === undefined ? opt.rowGroup : existingGridCol.rowGroup,
                rowGroupIndex: existingGridCol?.rowGroupIndex === undefined ? opt.rowGroupIndex : existingGridCol.rowGroupIndex
            };
        });
    }
};
